import React, { useState, useCallback, useRef, useEffect } from 'react';
import { 
  streamChatCompletion,
  isApiKeyConfigured,
  MODELS
} from '../../utils/openaiClient';
import MessageContent from './MessageContent';
import './OpenAIExample.css';

const OpenAIExample = () => {
  const [prompt, setPrompt] = useState('');
  const [conversationHistory, setConversationHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentStreamingMessage, setCurrentStreamingMessage] = useState('');
  const [tokenCount, setTokenCount] = useState(0);
  const [code, setCode] = useState(`// Welcome to Code Assistant!
// Edit this code and ask questions about it in the chat

function fibonacci(n) {
  if (n <= 1) return n;
  
  let prev = 0;
  let curr = 1;
  
  for (let i = 2; i <= n; i++) {
    const temp = curr;
    curr = prev + curr;
    prev = temp;
  }
  
  return curr;
}

// Example usage
console.log(fibonacci(10)); // Output: 55

// Try asking questions like:
// - "How does this function work?"
// - "Can you optimize this code?"
// - "What's the time complexity?"
// - "Can you add memoization?"`);
  
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const codeEditorRef = useRef(null);
  const containerRef = useRef(null);
  
  // Resizable panels state
  const [panelWidths, setPanelWidths] = useState(() => {
    const saved = localStorage.getItem('codeAssistantPanelWidths');
    return saved ? JSON.parse(saved) : { left: 50, right: 50 };
  });
  const [isResizing, setIsResizing] = useState(false);
  
  // Save panel widths to localStorage
  useEffect(() => {
    localStorage.setItem('codeAssistantPanelWidths', JSON.stringify(panelWidths));
  }, [panelWidths]);
  
  // Handle panel resize
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };
  
  const handleDoubleClick = () => {
    setPanelWidths({ left: 50, right: 50 });
  };
  
  const handleMouseMove = useCallback((e) => {
    if (!isResizing || !containerRef.current) return;
    
    const containerWidth = containerRef.current.offsetWidth;
    const x = e.clientX;
    const leftWidth = (x / containerWidth) * 100;
    const rightWidth = 100 - leftWidth;
    
    // Limit minimum panel widths
    if (leftWidth >= 20 && leftWidth <= 80) {
      setPanelWidths({ left: leftWidth, right: rightWidth });
    }
  }, [isResizing]);
  
  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);
  
  // Add event listeners for resizing
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    } else {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    // Only scroll on new messages, not streaming updates
    if (conversationHistory.length > 0 && !currentStreamingMessage) {
      scrollToBottom();
    }
  }, [conversationHistory]); // Remove currentStreamingMessage from dependencies

  // Simple token estimation (rough approximation)
  const estimateTokens = (text) => {
    // Rough estimate: ~4 characters per token on average
    // This is a simplified calculation - actual tokenization is more complex
    return Math.ceil(text.length / 4);
  };

  // System prompt for code assistance
  const CODE_SYSTEM_PROMPT = `You are a helpful coding assistant. The user has a code editor open with the following code:

\`\`\`javascript
${code}
\`\`\`

When answering questions, always consider the context of this code. Be specific and reference the actual code when explaining. If the user asks to modify the code, provide clear instructions or the modified version.

For optimization requests, provide your optimized code within a markdown code block. Do not provide JSON or multiple suggestions in a single response - focus on one high-quality optimization.`;

  // Use LLM to classify the user's intent
  const classifyUserIntent = async (message) => {
    const classificationPrompt = `Analyze the following user message and determine their intent. 
The user has code in their editor and is asking: "${message}"

Classify this as ONE of the following:
- OPTIMIZATION: User wants to improve, optimize, enhance, refactor, or make the code better/faster/more efficient
- SCORING: User wants to analyze, evaluate, score, or get metrics about their current code without modifications
- GENERAL: Any other type of question or request

Respond with only the classification word (OPTIMIZATION, SCORING, or GENERAL).`;

    let classification = 'GENERAL';
    try {
      await streamChatCompletion(
        classificationPrompt,
        (chunk, fullText) => {
          classification = fullText.trim().toUpperCase();
          setTokenCount(prev => prev + estimateTokens(chunk));
        },
        {
          model: MODELS.GPT4O,
          temperature: 0,
          maxTokens: 10,
          systemPrompt: "You are a classification assistant. Respond only with the classification."
        }
      );
    } catch (error) {
      console.error('Classification error:', error);
    }

    console.log('Intent classification:', { message, classification });
    return classification;
  };

  // Use LLM to generate dynamic metrics based on code and user request
  const generateDynamicMetrics = async (currentCode, userMessage) => {
    const metricsPrompt = `Given this code:
\`\`\`javascript
${currentCode}
\`\`\`

And this user request: "${userMessage}"

Generate 6 relevant metrics to evaluate code quality for this specific context. Consider what aspects would be most important for this type of code and the user's goals.

Return ONLY a JSON array with exactly 6 metrics in this format:
[
  {"name": "Metric Name", "description": "Brief description", "weight": 1.0}
]

Make the metrics specific to the code type and user's request. For example:
- If it's an algorithm, focus on complexity and efficiency
- If it's UI code, include user experience metrics
- If it's API code, include error handling and security
- Adjust based on what the user is asking for

Ensure weights sum to approximately 6.0.`;

    let metricsJson = [];
    let localTokenCount = 0;
    try {
      let response = '';
      await streamChatCompletion(
        metricsPrompt,
        (chunk, fullText) => {
          response = fullText;
          localTokenCount = estimateTokens(metricsPrompt + fullText);
          setTokenCount(prev => prev + estimateTokens(chunk));
        },
        {
          model: MODELS.GPT4O,
          temperature: 0.3,
          maxTokens: 500,
          systemPrompt: "You are a metrics generation assistant. Respond only with valid JSON."
        }
      );
      
      // Parse JSON from response
      const jsonMatch = response.match(/\[[\s\S]*\]/);
      if (jsonMatch) {
        metricsJson = JSON.parse(jsonMatch[0]);
      }
    } catch (error) {
      console.error('Error generating metrics:', error);
      // Fallback to default metrics
      metricsJson = [
        {"name": "Performance", "description": "Execution speed and efficiency", "weight": 1.0},
        {"name": "Memory Usage", "description": "Memory efficiency", "weight": 1.0},
        {"name": "Readability", "description": "Code clarity and maintainability", "weight": 1.0},
        {"name": "Correctness", "description": "Functional correctness", "weight": 1.0},
        {"name": "Best Practices", "description": "Following standards", "weight": 1.0},
        {"name": "Scalability", "description": "Ability to handle growth", "weight": 1.0}
      ];
    }
    
    return metricsJson;
  };

  // Generate dynamic optimization prompts based on code and user request
  const generateOptimizationApproaches = async (currentCode, userMessage) => {
    const approachesPrompt = `Given this code:
\`\`\`javascript
${currentCode}
\`\`\`

And this optimization request: "${userMessage}"

Generate 6 different optimization approaches that would be relevant for this specific code and request. Each approach should focus on a different aspect or technique.

Return ONLY a JSON array with exactly 6 approaches in this format:
[
  "First optimization approach focusing on...",
  "Second optimization approach focusing on...",
  ...
]

Make the approaches specific and relevant to:
1. The type of code (algorithm, UI, API, etc.)
2. The current implementation's weaknesses
3. The user's specific request
4. Different optimization dimensions (speed, memory, readability, etc.)`;

    let approaches = [];
    let localTokenCount = 0;
    try {
      let response = '';
      await streamChatCompletion(
        approachesPrompt,
        (chunk, fullText) => {
          response = fullText;
          localTokenCount = estimateTokens(approachesPrompt + fullText);
          setTokenCount(prev => prev + estimateTokens(chunk));
        },
        {
          model: MODELS.GPT4O,
          temperature: 0.7,
          maxTokens: 500,
          systemPrompt: "You are an optimization strategy assistant. Respond only with valid JSON."
        }
      );
      
      // Parse JSON from response
      const jsonMatch = response.match(/\[[\s\S]*\]/);
      if (jsonMatch) {
        approaches = JSON.parse(jsonMatch[0]);
      }
    } catch (error) {
      console.error('Error generating approaches:', error);
      // Fallback to generic approaches
      approaches = [
        "Focus on performance optimization and execution speed",
        "Focus on memory efficiency and space complexity",
        "Focus on code readability and maintainability",
        "Focus on modern best practices and patterns",
        "Focus on error handling and edge cases",
        "Focus on algorithmic improvements and data structures"
      ];
    }
    
    return approaches;
  };

  // Enhanced code analyzer that uses dynamic metrics
  const analyzeCodeWithMetrics = async (codeToAnalyze, metrics) => {
    const analysisPrompt = `Analyze this code:
\`\`\`javascript
${codeToAnalyze}
\`\`\`

Evaluate it based on these metrics:
${metrics.map(m => `- ${m.name}: ${m.description}`).join('\n')}

For each metric, provide a score from 1-10 and a brief reason.

Return ONLY a JSON array with scores in this format:
[
  {"name": "Metric Name", "score": 8, "description": "Brief reason for score"}
]

Be objective and specific in your scoring.`;

    let scores = [];
    let localTokenCount = 0;
    try {
      let response = '';
      await streamChatCompletion(
        analysisPrompt,
        (chunk, fullText) => {
          response = fullText;
          localTokenCount = estimateTokens(analysisPrompt + fullText);
          setTokenCount(prev => prev + estimateTokens(chunk));
        },
        {
          model: MODELS.GPT4O,
          temperature: 0.2,
          maxTokens: 600,
          systemPrompt: "You are a code analysis assistant. Respond only with valid JSON."
        }
      );
      
      // Parse JSON from response
      const jsonMatch = response.match(/\[[\s\S]*\]/);
      if (jsonMatch) {
        scores = JSON.parse(jsonMatch[0]);
      }
    } catch (error) {
      console.error('Error analyzing code:', error);
      // Fallback to basic analysis
      scores = metrics.map(m => ({
        name: m.name,
        score: Math.floor(Math.random() * 4) + 5,
        description: "Analysis unavailable"
      }));
    }
    
    return scores;
  };

  // Generate optimization prompt for different approaches
  const getOptimizationPrompt = (userMessage, approach, currentCode) => {
    return `Given this code:
\`\`\`javascript
${currentCode}
\`\`\`

User request: ${userMessage}

Please optimize this code with the following approach: ${approach}

Provide the optimized code in a markdown code block with a brief explanation of the changes.`;
  };

  // Parse code from LLM response
  const parseCodeFromResponse = (response) => {
    const codeBlockMatch = response.match(/```(?:\w+)?\n([\s\S]*?)```/);
    if (codeBlockMatch) {
      return codeBlockMatch[1].trim();
    }
    return null;
  };

  // Handle sending message
  const handleSendMessage = useCallback(async () => {
    if (!prompt.trim() || isLoading) return;
    
    const userMessage = prompt.trim();
    setPrompt('');
    setIsLoading(true);
    setTokenCount(0); // Reset token count for new request
    
    // Add user message to history
    const newUserMessage = { role: 'user', content: userMessage };
    const updatedHistory = [...conversationHistory, newUserMessage];
    setConversationHistory(updatedHistory);
    
    try {
      // Use LLM to classify the user's intent
      const intent = await classifyUserIntent(userMessage);
      
      // Handle based on intent
      if (intent === 'SCORING') {
        console.log('Processing scoring request...');
        
        // Generate dynamic metrics based on code and request
        setCurrentStreamingMessage('🎯 Analyzing your code and generating relevant metrics...');
        const dynamicMetrics = await generateDynamicMetrics(code, userMessage);
        
        // Analyze the current code with dynamic metrics
        setCurrentStreamingMessage('📊 Evaluating code against metrics...');
        const metrics = await analyzeCodeWithMetrics(code, dynamicMetrics);
        
        // Create a detailed analysis response
        const analysisText = `I've analyzed your current code. Here's a comprehensive evaluation:

**Overall Assessment:**
${metrics.map(m => `• ${m.name}: ${m.score}/10 - ${m.description}`).join('\n')}

**Average Score:** ${(metrics.reduce((sum, m) => sum + m.score, 0) / metrics.length).toFixed(1)}/10

The analysis shows ${metrics.filter(m => m.score >= 8).length} strong areas and ${metrics.filter(m => m.score < 6).length} areas that could be improved.`;
        
        // Create a structured response with the analysis
        const structuredResponse = {
          type: 'code-suggestions',
          text: analysisText,
          context: 'Code Analysis',
          suggestions: [{
            code: code,
            language: 'javascript',
            filename: 'current-code.js',
            explanation: 'Your current implementation',
            metrics: metrics
          }],
          tokensUsed: tokenCount
        };
        
        // Add assistant message to history
        const assistantMessage = { role: 'assistant', content: structuredResponse };
        setConversationHistory([...updatedHistory, assistantMessage]);
        setCurrentStreamingMessage('');
      }
      else if (intent === 'OPTIMIZATION') {
        console.log('Starting genetic algorithm optimization...');
        
        // Generate dynamic metrics and approaches
        setCurrentStreamingMessage('🎯 Analyzing your code and request to determine optimization strategies...');
        const [dynamicMetrics, optimizationApproaches] = await Promise.all([
          generateDynamicMetrics(code, userMessage),
          generateOptimizationApproaches(code, userMessage)
        ]);
        
        // Genetic Algorithm Implementation
        const generations = 3; // Number of generations
        const populationSize = 3; // Suggestions per generation
        const allSuggestions = [];
        let parentSuggestions = [];
        
        // Show initial message with discovered approaches
        setCurrentStreamingMessage(`🧬 Starting genetic algorithm optimization...

📋 Generated optimization approaches:
${optimizationApproaches.map((approach, i) => `${i + 1}. ${approach}`).join('\n')}

This will generate 9 solutions across 3 generations using these approaches.`);
        
        // Wait a moment to show the message
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        for (let gen = 0; gen < generations; gen++) {
          const currentGenSuggestions = [];
          
          // Update streaming message for current generation
          setCurrentStreamingMessage(`🤔 Generation ${gen + 1}/${generations}: Creating ${populationSize} optimized variants...`);
          
          // Generate prompts for this generation
          for (let i = 0; i < populationSize; i++) {
            let optimizationPrompt;
            
            if (gen === 0) {
              // First generation: Use the generated optimization approaches
              const approachIndex = i % optimizationApproaches.length;
              optimizationPrompt = getOptimizationPrompt(userMessage, optimizationApproaches[approachIndex], code);
            } else {
              // Subsequent generations: Build upon best performers
              const bestParents = parentSuggestions
                .sort((a, b) => {
                  const aScore = a.metrics.reduce((sum, m) => sum + m.score, 0) / a.metrics.length;
                  const bScore = b.metrics.reduce((sum, m) => sum + m.score, 0) / b.metrics.length;
                  return bScore - aScore;
                })
                .slice(0, 2); // Take top 2 performers
              
              // Create prompt that builds on best solutions
              optimizationPrompt = `${userMessage}\n\nGeneration ${gen + 1}: Improve upon these high-performing solutions:\n\n`;
              bestParents.forEach((parent, idx) => {
                optimizationPrompt += `Best Solution ${idx + 1} (Score: ${(parent.metrics.reduce((sum, m) => sum + m.score, 0) / parent.metrics.length).toFixed(1)}):\n`;
                optimizationPrompt += `\`\`\`javascript\n${parent.code}\n\`\`\`\n\n`;
              });
              optimizationPrompt += `Create a new variant that combines the best aspects or finds new improvements. Consider these metrics: ${dynamicMetrics.map(m => m.name).join(', ')}.`;
            }
            
            // Update progress
            setCurrentStreamingMessage(`🤔 Generation ${gen + 1}: Optimizing variant ${i + 1}/${populationSize}...`);
            
            let response = '';
            await streamChatCompletion(
              optimizationPrompt,
              (chunk, fullText) => {
                response = fullText;
                setTokenCount(prev => prev + estimateTokens(chunk));
              },
              {
                model: MODELS.GPT4O,
                temperature: gen === 0 ? 0.8 : 0.6, // Lower temperature for later generations
                maxTokens: 1500,
                systemPrompt: CODE_SYSTEM_PROMPT
              }
            );
            
            // Parse the response to extract code
            const extractedCode = parseCodeFromResponse(response);
            if (extractedCode) {
              const explanationMatch = response.match(/^([\s\S]*?)```/);
              const explanation = explanationMatch ? explanationMatch[1].trim() : `Generation ${gen + 1}, Variant ${i + 1}`;
              
              // Analyze with dynamic metrics
              const metrics = await analyzeCodeWithMetrics(extractedCode, dynamicMetrics);
              
              const suggestion = {
                code: extractedCode,
                language: 'javascript',
                filename: `gen${gen + 1}-variant${i + 1}.js`,
                explanation: explanation.split('\n')[0],
                metrics: metrics,
                generation: gen + 1
              };
              
              currentGenSuggestions.push(suggestion);
              allSuggestions.push(suggestion);
            }
          }
          
          // Update parent suggestions for next generation
          parentSuggestions = [...parentSuggestions, ...currentGenSuggestions];
          
          // Show generation summary
          if (gen < generations - 1) {
            const avgScore = currentGenSuggestions.reduce((sum, s) => {
              const score = s.metrics.reduce((mSum, m) => mSum + m.score, 0) / s.metrics.length;
              return sum + score;
            }, 0) / currentGenSuggestions.length;
            
            setCurrentStreamingMessage(`✅ Generation ${gen + 1} complete. Average score: ${avgScore.toFixed(1)}/10. 🧬 Evolving next generation...`);
            await new Promise(resolve => setTimeout(resolve, 1000)); // Brief pause to show message
          }
        }
        
        // Sort all suggestions by score
        allSuggestions.sort((a, b) => {
          const aScore = a.metrics.reduce((sum, m) => sum + m.score, 0) / a.metrics.length;
          const bScore = b.metrics.reduce((sum, m) => sum + m.score, 0) / b.metrics.length;
          return bScore - aScore;
        });
        
        // Create structured response with all generations
        const structuredResponse = {
          type: 'code-suggestions',
          text: `Genetic algorithm optimization complete! Generated ${allSuggestions.length} solutions across ${generations} generations:

**Optimization Metrics Used:**
${dynamicMetrics.map(m => `• ${m.name}: ${m.description}`).join('\n')}`,
          context: 'Genetic Algorithm Optimization',
          suggestions: allSuggestions,
          tokensUsed: tokenCount
        };
        
        // Add assistant message to history
        const assistantMessage = { role: 'assistant', content: structuredResponse };
        setConversationHistory([...updatedHistory, assistantMessage]);
        setCurrentStreamingMessage('');
      } else {
        // Regular (non-optimization) request - handle as before
        setCurrentStreamingMessage('');
        let fullResponse = '';
        
        await streamChatCompletion(
          userMessage,
          (chunk, fullText) => {
            fullResponse = fullText;
            setCurrentStreamingMessage(fullText);
            setTokenCount(prev => prev + estimateTokens(chunk));
          },
          {
            model: MODELS.GPT4O,
            temperature: 0.7,
            maxTokens: 1000,
            systemPrompt: CODE_SYSTEM_PROMPT
          }
        );
        
        // Add assistant message to history
        const assistantMessage = { role: 'assistant', content: fullResponse };
        setConversationHistory([...updatedHistory, assistantMessage]);
        setCurrentStreamingMessage('');
      }
    } catch (error) {
      const errorMessage = { role: 'assistant', content: `Error: ${error.message}` };
      setConversationHistory([...updatedHistory, errorMessage]);
      setCurrentStreamingMessage('');
    } finally {
      setIsLoading(false);
      setTokenCount(0); // Reset after completion
    }
  }, [prompt, conversationHistory, isLoading, code]);

  // Handle Enter key press in chat
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Handle Tab key in code editor
  const handleCodeKeyDown = (e) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const start = e.target.selectionStart;
      const end = e.target.selectionEnd;
      const value = e.target.value;
      setCode(value.substring(0, start) + '  ' + value.substring(end));
      // Reset cursor position after state update
      setTimeout(() => {
        e.target.selectionStart = e.target.selectionEnd = start + 2;
      }, 0);
    }
  };

  // Clear conversation
  const clearConversation = () => {
    setConversationHistory([]);
    setCurrentStreamingMessage('');
  };

  // Auto-resize chat textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
    }
  }, [prompt]);

  if (!isApiKeyConfigured()) {
    return (
      <div className="api-key-warning">
        <h3>OpenAI API Key Not Configured</h3>
        <p>Please set your OpenAI API key in the environment variable: <code>REACT_APP_OPENAI_API_KEY</code></p>
        <p>Create a <code>.env</code> file in your project root with:</p>
        <pre>
          REACT_APP_OPENAI_API_KEY=your_api_key_here
        </pre>
      </div>
    );
  }

  return (
    <div className={`openai-container ${isResizing ? 'resizing' : ''}`} ref={containerRef}>
      {/* Code Editor Panel */}
      <div className="code-editor-panel" style={{ width: `${panelWidths.left}%` }}>
        {/* Editor Header */}
        <div className="editor-header">
          <span className="file-icon">📄</span>
          <span>main.js</span>
          <span className="file-type">JavaScript</span>
        </div>

        {/* Code Editor */}
        <div className="code-editor-wrapper">
          <textarea
            ref={codeEditorRef}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            onKeyDown={handleCodeKeyDown}
            spellCheck={false}
            className="code-editor"
          />
        </div>
      </div>

      {/* Resizable Splitter */}
      <div 
        className={`panel-splitter ${isResizing ? 'resizing' : ''}`}
        onMouseDown={handleMouseDown}
        onDoubleClick={handleDoubleClick}
        title="Drag to resize panels. Double-click to reset to 50/50."
      >
        <div className="splitter-handle" />
      </div>

      {/* Chat Panel */}
      <div className="chat-panel" style={{ width: `${panelWidths.right}%` }}>
        {/* Chat Header */}
        <div className="chat-header">
          <div className="chat-header-title">
            <span className="chat-icon">💬</span>
            <span>Code Assistant</span>
          </div>
          {conversationHistory.length > 0 && (
            <button
              onClick={clearConversation}
              className="clear-chat-btn"
            >
              Clear Chat
            </button>
          )}
        </div>

        {/* Messages Container */}
        <div className="messages-container">
          {conversationHistory.length === 0 && !currentStreamingMessage && (
            <div className="welcome-message">
              <h3>Ask me about your code!</h3>
              <p>
                I can help you understand, debug, optimize, or refactor the code in the editor.
              </p>
              
              {/* Quick Actions */}
              <div className="quick-actions">
                <button
                  className="quick-action-btn score-btn"
                  onClick={() => {
                    const scoreMessage = "Please analyze and score my current code. I want to see metrics for performance, memory usage, readability, and maintainability without any modifications.";
                    setPrompt(scoreMessage);
                    handleSendMessage();
                  }}
                  disabled={isLoading}
                >
                  <span className="action-icon">📊</span>
                  Score Current Code
                </button>
                <button
                  className="quick-action-btn optimize-btn"
                  onClick={() => {
                    const optimizeMessage = "I want to optimize my code. Please suggest improvements to make it better, faster, and more efficient.";
                    setPrompt(optimizeMessage);
                    handleSendMessage();
                  }}
                  disabled={isLoading}
                >
                  <span className="action-icon">🚀</span>
                  Make It Better
                </button>
              </div>
              
              <div className="welcome-examples">
                <p>Try asking:</p>
                <ul>
                  <li>• "How does this function work?"</li>
                  <li>• "Can you find any bugs?"</li>
                  <li>• "How can I optimize this?"</li>
                  <li>• "Add error handling"</li>
                </ul>
              </div>
            </div>
          )}

          {conversationHistory.map((msg, index) => (
            <div
              key={index}
              className={`message-wrapper ${msg.role}`}
            >
              <div className={`message-bubble ${msg.role}`}>
                <MessageContent content={msg.content} />
              </div>
            </div>
          ))}

          {/* Streaming message */}
          {currentStreamingMessage && (
            <div className="message-wrapper assistant">
              <div className="message-bubble assistant">
                <MessageContent content={currentStreamingMessage} />
                <span className="streaming-cursor"></span>
              </div>
              {tokenCount > 0 && (
                <div className="token-counter">
                  <span className="token-icon">🪙</span>
                  <span className="token-count">{tokenCount.toLocaleString()} tokens</span>
                </div>
              )}
            </div>
          )}

          {/* Typing indicator */}
          {isLoading && !currentStreamingMessage && (
            <div className="message-wrapper assistant">
              <div className="message-bubble assistant">
                <div className="typing-indicator">
                  <span className="typing-dot"></span>
                  <span className="typing-dot"></span>
                  <span className="typing-dot"></span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Floating Quick Actions */}
        {conversationHistory.length > 0 && (
          <div className="floating-actions">
            <button
              className="floating-action-btn"
              onClick={() => {
                const scoreMessage = "Please analyze and score my current code. I want to see metrics for performance, memory usage, readability, and maintainability without any modifications.";
                setPrompt(scoreMessage);
                handleSendMessage();
              }}
              disabled={isLoading}
              title="Score Current Code"
            >
              📊
            </button>
            <button
              className="floating-action-btn"
              onClick={() => {
                const optimizeMessage = "I want to optimize my code. Please suggest improvements to make it better, faster, and more efficient.";
                setPrompt(optimizeMessage);
                handleSendMessage();
              }}
              disabled={isLoading}
              title="Make It Better"
            >
              🚀
            </button>
          </div>
        )}

        {/* Input Area */}
        <div className="input-area">
          <textarea
            ref={textareaRef}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask about the code..."
            disabled={isLoading}
            className="chat-input"
          />
          <button
            onClick={handleSendMessage}
            disabled={isLoading || !prompt.trim()}
            className="send-btn"
          >
            {isLoading ? 'Sending...' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OpenAIExample; 